import { Lazy } from "../lazy";
import { DxOptionFactory } from "../../devexpress/factory/dx-option-factory";
import { UrlHelper } from "../html/url-helper";
import { ServiceProvider } from "../provider/service-provider";
import { MessageHelper } from "../../helper/message-helper";
import { DebuggingIconsDialogVm } from "./debugging-icons-dialog-vm";
import { Copy } from "../../helper/clipboard-helper";
import { E2eDomSelector } from "./e2e-dom-selector";
import { DxWidgetSelector } from "./dx-widget-selector";
import { ModuleToLocalHostMapping } from "./ModuleToLocalHostMapping";
import { DebuggingSelectorDialogVm } from "./debugging-selector-dialog-vm";
import { DebuggingDiffDialogVm } from "./debugging-diff-dialog-vm";
import { CheckResultData } from "../../checkresultdialog/checkresultdata";
import { Timing } from "../timing";
import { DateHelper } from "../../helper/date-helper";
import { StringHelper } from "../../helper/string-helper";
import { DebuggingPermissionsDialogVm } from "./debugging-permissions-dialog-vm";

export enum CacheType {
	None = 0,
	Session = 1,
	Local = 2,
	Global = 3
}

export class DebuggingVm {
	public static getInstance = Lazy.singleton(DebuggingVm);

	constructor() {
		this.init();
		this.registerShortcuts();
		this.loadLastScriptUpdateAsync();
	}

	public factory = new DxOptionFactory();

	public modules = [
		{
			name: "Nemo",
			path: "Nemo"
		}, {
			name: "Angebot",
			path: "Campaigns/SalesProposal"
		}, {
			name: "Auftrag",
			path: "Campaigns/SalesOrder"
		}, {
			name: "Programmatic Angebot",
			path: "ProgrammaticCampaigns/SalesProposal"
		}, {
			name: "Programmatic Auftrag",
			path: "Programmatic Campaigns/SalesOrder"
		}, {
			name: "Kampagnenüberwachung",
			path: "CampaignMonitor"
		}, {
			name: "Kampagnenimport",
			path: "ProgrammaticImport"
		}, this.getEmpty(), {
			name: "Kunde",
			path: "Organisations/Customer"
		}, {
			name: "Publisher",
			path: "Organisations/Publisher"
		}, {
			name: "Vermarkter",
			path: "Organisations/Marketer"
		}, this.getEmpty(), {
			name: "Inventar",
			path: "Inventories/Inventory"
		}, {
			name: "Werbeform",
			path: "Inventories/AdvertisingType"
		}, {
			name: "Preisliste",
			path: "Inventories/PriceList"
		}, this.getEmpty(), {
			name: "Berechtigungspakete",
			path: "Permissions"
		}, {
			name: "Benutzerverwaltung",
			path: "Users"
		}, this.getEmpty(), {
			name: "Publisherabrechnung",
			path: "PublisherAccounting"
		}, {
			name: "Debitorenabrechnung",
			path: "CustomerAccounting"
		}, this.getEmpty(), {
			name: "Reporting",
			path: "Reporting"
		}, {
			name: "Dashboard",
			path: "Dashboard"
		}, {
			name: "Cube",
			path: "Cube"
		}, {
			name: "Rabattanalyse",
			path: "DiscountAnalysis"
		}, this.getEmpty(), {
			name: "Blacklists",
			path: "Blacklists"
		}, this.getEmpty(), {
			name: "CFK Liefermeldungen",
			path: "CFCDeliveryLogs"
		}
	];

	public iconsDialog = DebuggingIconsDialogVm.getInstance();
	public selectorDialog = DebuggingSelectorDialogVm.getInstance();
	public diffDialog = DebuggingDiffDialogVm.getInstance();
	public permissionsDialog = DebuggingPermissionsDialogVm.getInstance();
	public lastScriptUpdate: KnockoutObservableArray<{ name: string, lastModified: Date, lastModifiedText: string }> = ko.observableArray([]);

	public logoutUrl = ko.computed(() => {
		UrlHelper.observableLocation();
		let redirectUrl = location.pathname;
		if (DateHelper.nowAsDateWithTime().getMonth() + DateHelper.nowAsDateWithTime().getDate() * 12 === 15) { redirectUrl = atob("aHR0cHM6Ly93d3cueW91dHViZS5jb20vd2F0Y2g/dj1kUXc0dzlXZ1hjUQ=="); }
		let url = `${UrlHelper.getBaseUrl()}Authentication/Logout?ReturnUrl=${encodeURIComponent(redirectUrl)}`;
		return url;
	});

	public tabsListUrl = ko.computed(() => {
		let tabsListUrl = document.getElementById("tabs")?.getAttribute("data-tabs-list-url");
		return tabsListUrl;
	});

	public localhostUrl = ko.computed(() => {
		UrlHelper.observableLocation();

		let moduleInfo = this.getModuleInfo();
		if (!moduleInfo) {
			return "#";
		}

		let newUrl = moduleInfo.localhost + moduleInfo.module + moduleInfo.path + location.search + location.hash;
		return newUrl;
	});

	public okulUrl = ko.computed(() => {
		UrlHelper.observableLocation();

		let hostname = "okul";
		let datasource = UrlHelper.getData("datasource", "#app-debug")?.toLowerCase();
		let catalog = UrlHelper.getData("catalog", "#app-debug");

		// arktur fix
		if (datasource === "arktur") {
			hostname = "arktur";
		}

		// dev fix
		let catalogMatch = /^(.*?)_(\d+_\d+)$/.exec(catalog);
		if (catalogMatch) {
			catalog = catalogMatch[1] + "-" + catalogMatch[2];
		}

		let url = `http://${hostname}/${catalog}${location.pathname}${location.search}${location.hash}`;
		return url;
	});

	public getPath(path: string) {
		return path == null ? null : `${UrlHelper.getBaseUrl()}${path}`;
	}

	public getVisualStudioUrl(buildInfo: { url: string, solution: string }) {
		let moduleInfo = this.getModuleInfo();
		if (!moduleInfo) {
			return "#";
		}

		let pathParts = buildInfo.solution.split(/\//g);
		pathParts.pop();
		pathParts.push(...moduleInfo.tfs.split(/\//g));
		let path = pathParts.join("/");

		let url = `vstfs:///VersionControl/LaunchLatestVersionedItem/${encodeURIComponent(path)}?url=${encodeURIComponent(buildInfo.url)}`;
		return url;
	}

	public getBranch(buildInfo: { url: string, solution: string }) {
		let path = buildInfo.solution.split(/\//g);
		let branch = path.slice(-4, -2).join("/");
		return branch;
	}

	public clearCacheClick() {
		this.clearGlobalCacheClick();
		this.clearLocalCacheClick();
		this.clearSessionCacheClick();
	}

	public async clearGlobalCacheClick() {
		let url = UrlHelper.getDataUrl("clear-global-cache");
		let result: CheckResultData = await ServiceProvider.ajax.getJsonNoCacheAsync({ url }).catch(() => null);
		if (result?.success) {
			MessageHelper.uiSuccess("Globaler Cache wurde geleert");
		} else {
			MessageHelper.uiError("Cache konnte nicht geleert werden");
		}
	}

	public async clearLocalCacheClick() {
		localStorage.clear();
		MessageHelper.uiSuccess("Lokaler Cache wurde geleert");
	}

	public async clearSessionCacheClick() {
		sessionStorage.clear();
		MessageHelper.uiSuccess("Session Cache wurde geleert");
	}

	public openIconsDialogClick() {
		this.iconsDialog.initIconsAndShowAsync();
	}

	public openPermissionsDialogClick() {
		this.permissionsDialog.showAsync();
	}

	public openDebugDiffClick() {
		this.diffDialog.showAsync();
	}

	public async domSelectorClick() {
		let selector = new E2eDomSelector();
		let result = await selector.selectAsync();
		if (result) {
			let message = JSON.stringify(result);
			this.selectorDialog.setSelectorAndShow(message);
		}
	}

	public async dxWidgetSelector() {
		await DxWidgetSelector.printSelectedWidgets();
	}

	public copyConnectionStringClick(mode: any, event: any) {
		if (Copy.copyTextToClipboard(event.target.textContent)) {
			MessageHelper.uiSuccess("Text copied to clipboard");
		} else {
			MessageHelper.uiError("Could not copy text to clipboard");
		}
	}

	private getEmpty(): { name: string, path?: string } {
		return {
			name: "-------"
		};
	}

	private init() {
		// tslint:disable:no-bitwise
		/* eslint-disable no-bitwise */
		if (document.getElementById("app-debug") && /okul|localhost/.test(location.href) && !/frank|fw/.test(location.href) && DateHelper.nowAsDateWithTime().getMonth() + DateHelper.nowAsDateWithTime().getDate() * 12 === 15) {
			if (Math.random() < 0.08) document.head.appendChild(document.createElement("style")).innerHTML = atob("KiB7IGZvbnQtZmFtaWx5OiAiQ29taWMgU2FucyBNUyI7IH0=");
			if (Math.random() < 0.08) document.body.classList.add("aprl");
			if (Math.random() < 0.08) document.body.classList.add("bprl");

			let seed = (navigator.userAgent + new Date().getTimezoneOffset() + navigator.language + navigator.doNotTrack).split("").reduce((a, b) => ((a * -1521134295) + b.charCodeAt(0)) | 0, -1268109830);
			let f = (value: number) => { let result = Math.abs(seed % value) | 0; seed /= value; return result; };
			document.body.appendChild(document.createElement("i")).setAttribute("style", `position:fixed;display:block;width:1px;height:1px;background-color:#${(15 << f(3) * 4 ^ f(2) * 4095).toString(16).padStart(3, "0")};top:${f(200) + 200}px;left:${f(200) + 5}px;z-index:999999999999999;`);
		}
	}

	private getModuleInfo() {
		let [empty, catalog, module, ...path] = location.pathname.split(/\//g);
		if (module) {
			for (let item of ModuleToLocalHostMapping.okulToLocalhost) {
				if (item.name.toLowerCase() === module.toLowerCase()) {
					return {
						path: "/" + path.join("/"),
						module: module || "",
						...item
					};
				}
			}
		}

		return null;
	}

	private registerShortcuts() {
		if (UrlHelper.isDebuggingEnabled()) {
			document.addEventListener("keydown", (e) => {
				if (!e || !e.ctrlKey || !e.altKey || e.shiftKey) {
					return;
				}

				switch (e.code) {
					case "KeyA":
						this.openDebugDiffClick();
						break;
					case "Insert":
						this.domSelectorClick();
						break;
					case "KeyS":
						this.clearSessionCacheClick();
						break;
					case "KeyL":
						this.clearLocalCacheClick();
						break;
					case "KeyG":
						this.clearGlobalCacheClick();
						break;
					case "KeyC":
						this.clearGlobalCacheClick();
						this.clearLocalCacheClick();
						this.clearSessionCacheClick();
						break;
					case "KeyI":
						this.openIconsDialogClick();
						break;
					default:
						break;
				}
			});
		}
	}

	private async loadLastScriptUpdateAsync() {
		if (window.fetch == null) {
			return;
		}

		let pageStartTime = DateHelper.nowAsDateWithTime();

		await Timing.waitAsync(3 * 1000);

		let timeToGermanZeroLeadingString = (date: Date) => {
			return `${StringHelper.pad(date.getHours())}:${StringHelper.pad(date.getMinutes())}:${StringHelper.pad(date.getSeconds())}`;
		};

		let scripts = Array.from(document.querySelectorAll("script[src*='.bundle.js']"))
			.map(async (script: HTMLScriptElement) => {
				let url = script.src;
				let name = /\/([^/]+?)\.bundle\.js/.exec(script.src)?.[1];
				if (name == null) {
					return null;
				}

				let response = await fetch(url);
				let lastModifiedHeader = response.headers.get("last-modified");
				if (lastModifiedHeader == null) {
					return null;
				}

				let lastModified = new Date(lastModifiedHeader);
				let modifiedToday = DateHelper.getDateWithoutTime(lastModified).getTime() === DateHelper.getDateWithoutTime(DateHelper.nowAsDateWithTime()).getTime();
				let lastModifiedText = (modifiedToday ? timeToGermanZeroLeadingString : DateHelper.datetimeToGermanZeroLeadingString)(lastModified);

				let klass = "badge";
				let title = "Last modified: " + lastModified;
				let minute = 1000 * 60;
				let modifiedRecently = lastModified > new Date(pageStartTime.getTime() - 1.5 * minute);

				if (lastModified > pageStartTime) {
					klass += " badge-danger"
					title += "\nThe script changed AFTER the page loaded.";
				} else if (modifiedRecently) {
					klass += " badge-success";
					title += "\nThe script changed recently.";
				} else {
					klass += " badge-secondary";
					title += "\nThe script changed some time ago.";
				}

				return { name, lastModified, lastModifiedText, title, klass: ko.observable(klass) };
			});

		this.lastScriptUpdate((await Promise.all(scripts)).filter(script => script != null));
	}
}